package one.healthium.ui.compose.theme

import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.Immutable
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import one.healthium.ui.compose.components.AppTopBarDefaults
import one.healthium.ui.compose.components.AppTopBarStyle
import one.healthium.ui.compose.theme.styles.AppButtonStyle
import one.healthium.ui.compose.theme.styles.AppButtonStyleRef
import one.healthium.ui.compose.theme.styles.AppNavigationBarStyle
import one.healthium.ui.compose.theme.styles.AppNavigationRailStyle
import one.healthium.ui.compose.theme.styles.BannerStyle
import one.healthium.ui.compose.theme.styles.ChartStyle
import one.healthium.ui.compose.theme.styles.ContentCardStyle
import one.healthium.ui.compose.theme.styles.DailyTaskItemStyle
import one.healthium.ui.compose.theme.styles.DailyTaskStyle
import one.healthium.ui.compose.theme.styles.HealthKitBannerStyle
import one.healthium.ui.compose.theme.styles.LogControlStyle
import one.healthium.ui.compose.theme.styles.LogWaterControlStyle
import one.healthium.ui.compose.theme.styles.LogWeightControlStyle
import one.healthium.ui.compose.theme.styles.ProfileStyle
import one.healthium.ui.compose.theme.styles.TutorialStyle
import one.healthium.ui.compose.theme.styles.UScreenStyle

object Themes {

    @Composable
    fun ifa() = AppThemeBase(
        colors = Colors.ifa,
        shapes = Shapes.carni,
        textStyles = AppTextStyle(
            fontFamily = null,
            fontFamilySecondary = null,
        ),
    )

    @Composable
    fun carnivore(fontFamily: FontFamily? = null) = AppThemeBase(
        colors = Colors.carnivore,
        shapes = Shapes.carni,
        textStyles = AppTextStyle(
            fontFamily = fontFamily,
            fontFamilySecondary = fontFamily,
            textField = TextStyle(
                fontFamily = fontFamily,
                fontSize = 38.sp,
                fontWeight = FontWeight.Light,
            ),
        ),
        appTopBarStyle = AppTopBarStyle(
            colors = AppTopBarDefaults.colors(
                containerColor = yellow_2,
                titleContentColor = black,
                navigationIconContentColor = black,
            ),
        ),
        navigationRailStyle = AppNavigationRailStyle(
            containerColor = red_9,
            defaultLabelColor = white60,
            activeLabelColor = white,
        ),
    )

    @Composable
    fun pcos(fontFamily: FontFamily? = null) = AppThemeBase(
        colors = Colors.pcos,
        shapes = Shapes.pcos,
        textStyles = AppTextStyle(
            fontFamily = fontFamily,
            fontFamilySecondary = fontFamily,
            body = TextStyle(
                fontFamily = fontFamily,
                fontSize = 18.sp,
                fontWeight = FontWeight.Normal,
                lineHeight = 24.sp,
                letterSpacing = -(0.18).sp
            ),
            bodySemiBold = TextStyle(
                fontFamily = fontFamily,
                fontSize = 18.sp,
                fontWeight = FontWeight.SemiBold,
                lineHeight = 24.sp,
                letterSpacing = -(0.18).sp
            ),
            title20Bold = TextStyle(
                fontFamily = fontFamily,
                fontSize = 20.sp,
                fontWeight = FontWeight.Bold,
            ),
            secondary_22_700 = TextStyle(
                fontFamily = fontFamily,
                fontSize = 22.sp,
                fontWeight = FontWeight.Bold,
            ),
            titleExtraBold = TextStyle(
                fontFamily = fontFamily,
                fontSize = 22.sp,
                fontWeight = FontWeight.ExtraBold,
            ),
            description = TextStyle(
                fontFamily = fontFamily,
                fontSize = 17.sp,
                fontWeight = FontWeight.Normal,
            ),
        ),
        profileStyle = ProfileStyle(
            avatarContainerColor = orange_3,
        ),
        navigationRailStyle = AppNavigationRailStyle(
            containerColor = red_10,
            defaultLabelColor = white,
            activeLabelColor = yellow_9,
        ),
    )

    @Composable
    fun med() = AppThemeBase(
        colors = Colors.med,
        shapes = Shapes.med,
        textStyles = AppTextStyle(
            fontFamily = null,
            fontFamilySecondary = null,
        ),
        profileStyle = ProfileStyle(
            avatarContainerColor = orange_3,
        ),
        navigationRailStyle = AppNavigationRailStyle(
            containerColor = green_11,
            defaultLabelColor = white,
            activeLabelColor = Colors.med.primary,
        ),
    )

    @Composable
    fun bland(fontFamily: FontFamily? = null) = AppThemeBase(
        colors = Colors.bland,
        shapes = Shapes.bland,
        textStyles = AppTextStyle(
            fontFamily = fontFamily,
            fontFamilySecondary = fontFamily,
            textField = TextStyle(
                fontFamily = fontFamily,
                fontSize = 38.sp,
                fontWeight = FontWeight.Light,
            ),
        ),
        navigationBarStyle = AppNavigationBarStyle(
            containerColor = Colors.bland.primary,
            defaultLabelColor = white,
            activeLabelColor = yellow_9,
        ),
        navigationRailStyle = AppNavigationRailStyle(
            containerColor = red_11,
            defaultLabelColor = white,
            activeLabelColor = yellow_9,
        ),
        logWaterControlStyle = LogWaterControlStyle(
            saveButtonStyleRef = AppButtonStyleRef.Primary,
            progressColor = Colors.bland.secondary,
        ),
        dailyTasksStyle = DailyTaskStyle(
            progressBarColor = white,
            progressBarTrackColor = black15,
            itemStyle = DailyTaskItemStyle.V2(
                iconTint = Colors.bland.secondary.copy(alpha = 0.5f),
            )
        ),
        appTopBarStyle = AppTopBarStyle(
            style = AppTheme.textStyles.secondary_17_600
        ),
        profileStyle = ProfileStyle(
            cardShape = RoundedCornerShape(12.dp),
            weightProgressBarStyle = ChartStyle(
                barColor = white,
                highlightedBarColor = Colors.bland.primary,
            ),
            weightProgressChartStyle = ChartStyle(
                barColor = Colors.bland.secondary,
                highlightedBarColor = Colors.bland.primary,
                barShape = RoundedCornerShape(6.dp),
                cardShape = RoundedCornerShape(30.dp),
            ),
            caloriesChartStyle = ChartStyle(
                barColor = Colors.bland.secondary,
                highlightedBarColor = Colors.bland.primary,
                barShape = RoundedCornerShape(6.dp),
                cardShape = RoundedCornerShape(30.dp),
            ),
            avatarContainerColor = Colors.bland.secondary,
            avatarContentColor = Colors.bland.onSecondary,
        )
    )

    @Composable
    fun life(fontFamily: FontFamily? = null) = AppThemeBase(
        colors = Colors.life,
        shapes = Shapes.life,
        textStyles = AppTextStyle(
            fontFamily = fontFamily,
            fontFamilySecondary = fontFamily,
            textField = TextStyle(
                fontFamily = fontFamily,
                fontSize = 38.sp,
                fontWeight = FontWeight.Light,
            ),
        ),
        navigationBarStyle = AppNavigationBarStyle(
            containerColor = Colors.life.primary,
            defaultLabelColor = Colors.life.onPrimary,
            activeLabelColor = Colors.life.secondary,
        ),
        logWaterControlStyle = LogWaterControlStyle(
            saveButtonStyleRef = AppButtonStyleRef.Primary,
            progressColor = Colors.life.secondary,
        ),
        healthKitBannerStyle = HealthKitBannerStyle(
            contentColor = black,
        ),
        profileStyle = ProfileStyle(
            cardShape = RoundedCornerShape(12.dp),
            weightProgressBarStyle = ChartStyle(
                barColor = Colors.life.primary.copy(alpha = 0.15f),
                highlightedBarColor = Colors.life.primary,
            ),
            weightProgressChartStyle = ChartStyle(
                barColor = Colors.life.secondary,
                highlightedBarColor = Colors.life.primary,
                barShape = RoundedCornerShape(6.dp),
                cardShape = RoundedCornerShape(30.dp),
            ),
            caloriesChartStyle = ChartStyle(
                barColor = Colors.life.secondary,
                highlightedBarColor = Colors.life.primary,
                barShape = RoundedCornerShape(6.dp),
                cardShape = RoundedCornerShape(30.dp),
            ),
            settingButtonTint = Colors.life.secondary,
        ),
        appTopBarStyle = AppTopBarStyle(
            lightStatusBar = true,
        ),
        navigationRailStyle = AppNavigationRailStyle(
            containerColor = Colors.life.primary,
            defaultLabelColor = white,
            activeLabelColor = Colors.life.secondary,
        ),
    )
}

@Immutable
class AppThemeBase(
    val colors: AppColors,
    val shapes: AppShapes,
    val textStyles: AppTextStyle,
    val appTopBarStyle: AppTopBarStyle? = null,
    val dailyTasksStyle: DailyTaskStyle = DailyTaskStyle(),
    val contentCardStyle: ContentCardStyle? = null,
    val logWaterControlStyle: LogWaterControlStyle = LogWaterControlStyle(),
    val logWeightControlStyle: LogWeightControlStyle? = null,
    val logControlStyle: LogControlStyle? = null,
    val profileStyle: ProfileStyle = ProfileStyle(),
    val tutorialStyle: TutorialStyle? = null,
    val defaultButtonStyle: AppButtonStyle? = null,
    val navigationBarStyle: AppNavigationBarStyle = AppNavigationBarStyle(),
    val navigationRailStyle: AppNavigationRailStyle = AppNavigationRailStyle(),
    val healthKitBannerStyle: HealthKitBannerStyle = HealthKitBannerStyle(),
    val bannerStyle: BannerStyle = BannerStyle(),
    val uscreenStyle: UScreenStyle = UScreenStyle(),
)
